import {
    AdminPermission,
    IRoleOutputDto,
    OrganizationPermissions,
    UsersPermissions,
} from '@quadminds/shared/auth-system';
import { ApplicationsEnum, StandardUserRoles, StorkAdminRoles } from '@quadminds/shared/commons';
import { StandardStorkRoles } from '../enums';

export class RolesHelper {
    static getAdminRoles(): string[] {
        return [StorkAdminRoles.OrganizationAdmin, StorkAdminRoles.StorkAdmin];
    }
}

export function findTopPriorityStorkRole(roles: IRoleOutputDto[]): IRoleOutputDto | undefined {
    let role: IRoleOutputDto | undefined;

    //Para que el orden de roles no influya en el rol seleccionado, solo la jerarquia
    const roleHierarchy: StandardStorkRoles[] = [
        StandardStorkRoles.ADMIN,
        StandardStorkRoles.EDITOR,
        StandardStorkRoles.DEFAULT,
    ];

    for (const priorityRole of roleHierarchy) {
        role = roles.find((r) => r.name === priorityRole);
        if (role) break;
    }

    if (!role) {
        role = roles.find((r) => r.name.includes(`${ApplicationsEnum.STORK}`));
    }

    if (!role) return;

    return role;
}

export function findTopPriorityOrganizationRole(roles: IRoleOutputDto[]): IRoleOutputDto | undefined {
    let role: IRoleOutputDto | undefined;

    //Para que el orden de roles no influya en el rol seleccionado, solo la jerarquia
    const roleHierarchy: StandardUserRoles[] = [StandardUserRoles.ADMIN, StandardUserRoles.DEFAULT];

    for (const priorityRole of roleHierarchy) {
        role = roles.find((r) => r.name === priorityRole);
        if (role) break;
    }

    if (!role) {
        role = roles.find((r) => !r.name.includes(`${ApplicationsEnum.STORK}`));
    }

    return role;
}

export interface IRoleGrantor {
    roles?: string[];
    permissions?: string[];
}

export interface IRoleGrant {
    roles?: string[];
    permissions?: string[];
}

export interface IRoleGrantViolations {
    roles: string[];
    permissions: string[];
}

// Jerarquia de roles de Stork: un usuario solo puede asignar roles de Stork de nivel menor o igual al propio
const storkRolesRank: Record<string, number> = {
    [StandardStorkRoles.DEFAULT]: 1,
    [StandardStorkRoles.EDITOR]: 2,
    [StandardStorkRoles.ADMIN]: 3,
};

const MAX_STORK_ROLE_RANK = 3;

// Rol basico que tiene todo usuario de la organizacion (solo lectura del usuario y la organizacion)
const alwaysGrantableRoles: string[] = [StandardUserRoles.DEFAULT];

// Permisos basicos que el front agrega a todo usuario, o que el BFF deriva de los permisos de choferes
const alwaysGrantablePermissions: string[] = [
    UsersPermissions.GetUser,
    UsersPermissions.UpdateUser,
    OrganizationPermissions.GetOrganization,
    OrganizationPermissions.UpdateDriverUsers,
    OrganizationPermissions.DeleteDriverUsers,
    OrganizationPermissions.OrganizationsInvitesDriversCreate,
    OrganizationPermissions.OrganizationsInvitesDriversUpdate,
    OrganizationPermissions.OrganizationsInvitesDriversDelete,
];

export function getStorkRoleRank(roles: string[] = []): number {
    return roles.reduce((rank, role) => Math.max(rank, storkRolesRank[role] ?? 0), 0);
}

export function isOrganizationAdmin(roles: string[] = []): boolean {
    return roles.includes(StandardUserRoles.ADMIN);
}

/**
 * Admin total: tiene el permiso `admin`, o es admin de Stork y de la organizacion a la vez
 */
export function hasFullGrantAccess(grantor: IRoleGrantor): boolean {
    return (
        !!grantor.permissions?.includes(AdminPermission.HasAdminPrivilege) ||
        (getStorkRoleRank(grantor.roles) === MAX_STORK_ROLE_RANK && isOrganizationAdmin(grantor.roles))
    );
}

function canGrantRole(grantor: IRoleGrantor, role: string): boolean {
    if (alwaysGrantableRoles.includes(role)) return true;
    if (storkRolesRank[role]) return storkRolesRank[role] <= getStorkRoleRank(grantor.roles);
    if (role === StandardUserRoles.ADMIN) return isOrganizationAdmin(grantor.roles);
    // Roles desconocidos solo los puede asignar un admin total
    return false;
}

/**
 * Devuelve los roles y permisos de `grant` que exceden las credenciales de `grantor`.
 * Los roles de Stork y de organizacion se comparan cada uno en su propia jerarquia,
 * y los permisos sueltos tienen que ser un subconjunto de los del grantor.
 */
export function getRoleGrantViolations(grantor: IRoleGrantor, grant: IRoleGrant): IRoleGrantViolations {
    if (hasFullGrantAccess(grantor)) return { roles: [], permissions: [] };

    const grantorPermissions = new Set([...(grantor.permissions ?? []), ...alwaysGrantablePermissions]);

    return {
        roles: (grant.roles ?? []).filter((role) => !canGrantRole(grantor, role)),
        permissions: (grant.permissions ?? []).filter((permission) => !grantorPermissions.has(permission)),
    };
}

export function canGrantRolesAndPermissions(grantor: IRoleGrantor, grant: IRoleGrant): boolean {
    const violations = getRoleGrantViolations(grantor, grant);
    return !violations.roles.length && !violations.permissions.length;
}

/**
 * Un usuario solo puede editar o eliminar usuarios e invitaciones de su mismo nivel o menor.
 * A diferencia de `getRoleGrantViolations`, los roles personalizados del objetivo no se rechazan
 * por nombre: se comparan por los permisos que otorgan.
 */
export function canManageCredentials(manager: IRoleGrantor, target: IRoleGrant): boolean {
    if (hasFullGrantAccess(manager)) return true;
    if (getStorkRoleRank(target.roles) > getStorkRoleRank(manager.roles)) return false;
    if (isOrganizationAdmin(target.roles) && !isOrganizationAdmin(manager.roles)) return false;

    const managerPermissions = new Set([...(manager.permissions ?? []), ...alwaysGrantablePermissions]);
    return (target.permissions ?? []).every((permission) => managerPermissions.has(permission));
}
